/** Merge under the top-level `projectSettings` key in each locale. */
export const projectSettingsCopy = {
  en: {
    breadcrumb: 'Project settings',
    viewProject: 'View project',
    sectionsLabel: 'Settings sections',
    sections: {
      general: 'General',
      access: 'Access',
      classification: 'Data classification & AI policy',
      danger: 'Danger zone',
    },
    errors: {
      loadTitle: 'Project settings could not be loaded',
      loadBody: 'Check your connection and try again.',
      retry: 'Try again',
    },
    form: {
      save: 'Save changes',
      saving: 'Saving…',
      saved: 'Saved',
      discard: 'Discard changes',
      unsaved: 'Unsaved changes',
      refreshed: 'This project was updated elsewhere. Your unsaved edits were kept.',
    },
    general: {
      title: 'General',
      description: 'Basic information about this project.',
      name: 'Project name',
      descriptionLabel: 'Description',
      counter: '{{count}}/{{max}}',
      status: 'Status',
      statusHelp: {
        ACTIVE: 'Active projects appear in your team’s project lists.',
        ARCHIVED: 'Archived projects are marked as archived in project lists.',
      },
      errors: {
        PROJECT_CONFLICT:
          'This project changed since you opened it. Reload the page and try again.',
        ACCESS_DENIED: 'You do not have permission to change this project.',
        default: 'The project could not be saved. Try again.',
      },
      success: 'Project settings saved.',
    },
    access: {
      title: 'Access',
      description: 'Choose who can view and edit this project.',
      manage: 'Manage access',
      restricted: 'You do not have permission to manage access to this project.',
    },
    classification: {
      title: 'Data classification & AI policy',
      description:
        'The classification controls which external AI and text-to-speech providers may receive this project’s content.',
      current: 'Current',
      levels: {
        NON_SENSITIVE: {
          label: 'Non-sensitive',
          description:
            'Public or low-risk content. It may be sent to external AI and text-to-speech providers your company allows for non-sensitive data.',
        },
        INTERNAL: {
          label: 'Internal',
          description:
            'Internal business content. It is only sent to providers your company has approved for internal data.',
        },
        CONFIDENTIAL: {
          label: 'Confidential',
          description:
            'Restricted content. It is only sent to providers your company has explicitly approved for confidential data.',
        },
      },
      policyNoteTitle: 'Your company’s AI data policy decides',
      policyNote:
        'A less restrictive level does not by itself make AI or narration available: your company’s AI data policy and provider settings still apply. A more restrictive level can stop AI and narration for this project.',
      lowerOwnerOnly: 'Only an Owner or Admin can make a project less restrictive.',
      readOnly: 'You can view the classification but you cannot change it.',
      proposals: {
        title: 'Classification change requests',
        description:
          'Owner and Admin review is available only when the proposal and decision gates are enabled. Classification changes do not enable providers; company policy still applies.',
        empty: 'There are no classification requests to review.',
        loadError: 'Classification requests could not be loaded. Try again.',
        transition: '{{from}} → {{to}}',
        requester: 'Requested by {{name}}',
        providerImpact:
          'Current runtime provider: {{provider}} · data policy: {{policy}}. {{eligible}} Company provider and narration (TTS) eligibility are shown below and bound to the approval.',
        mayBecomeEligible:
          'The target level may make this project eligible for that provider under the current policy.',
        noNewEligibility:
          'This transition does not make the current runtime AI provider newly eligible under the current policy.',
        eligibility:
          'AI text eligible: {{textBefore}} → {{textAfter}} · narration (TTS) eligible: {{ttsBefore}} → {{ttsAfter}}.',
        policyChanged:
          'The AI or narration provider configuration changed since submission. Approval is blocked; reject this proposal and submit a new one.',
        yes: 'yes',
        no: 'no',
        expires: 'Expires {{date}}',
        stale: 'The project changed after this request was submitted. It cannot be applied.',
        approve: 'Apply change',
        reject: 'Reject',
        paginationLabel: 'Pending classification request pages',
        previousPage: 'Previous page',
        nextPage: 'Next page',
        pageOf: 'Page {{page}} of {{total}}',
        status: {
          APPLIED: 'Applied',
          REJECTED: 'Rejected',
          EXPIRED: 'Expired',
          PENDING_REVIEW: 'Pending review',
        },
      },
      save: 'Save classification',
      saved: 'Classification updated.',
      confirmTitle: 'Make this project less restrictive?',
      confirmBody:
        '“{{name}}” will change from {{from}} to {{to}}. Content already in this project, including notes, scripts and knowledge, may become eligible to be sent to external AI and text-to-speech providers. Existing content is not reviewed or changed.',
      confirmResponsibility:
        'By continuing you confirm that this project’s content is appropriate for the new level.',
      confirmAction: 'Make less restrictive',
      errors: {
        ACCESS_DENIED: 'Only an Owner or Admin can make a project less restrictive.',
        PROJECT_CLASSIFICATION_CONFIRMATION_REQUIRED:
          'Confirm the change to make this project less restrictive.',
        PROJECT_CONFLICT:
          'This project changed since you opened it. Reload the page and try again.',
        default: 'The classification could not be saved. Try again.',
      },
    },
    danger: {
      title: 'Danger zone',
      description: 'Irreversible actions for this project.',
      deleteLabel: 'Delete this project',
      deleteHelp:
        'Removes the project and its presentations from active views. Published history and analytics may be retained according to policy.',
      deleteButton: 'Delete project',
      dialogTitle: 'Delete project?',
      dialogBody:
        'This removes {{name}} and its presentations from active views. Published history and analytics may be retained according to policy.',
      dialogConfirm: 'Delete project',
      deleting: 'Deleting…',
      errors: {
        PROJECT_CONFLICT:
          'This project changed since you opened it. Reload the page and try again.',
        default: 'The project could not be deleted. Try again.',
      },
    },
    overview: {
      editClassification: 'Edit classification',
      viewClassification: 'View classification',
      settings: 'Project settings',
    },
    menu: {
      open: 'Open project',
      settings: 'Project settings',
    },
  },
  fa: {
    breadcrumb: 'تنظیمات پروژه',
    viewProject: 'مشاهده پروژه',
    sectionsLabel: 'بخش‌های تنظیمات',
    sections: {
      general: 'عمومی',
      access: 'دسترسی',
      classification: 'طبقه‌بندی داده و سیاست هوش مصنوعی',
      danger: 'ناحیهٔ خطر',
    },
    errors: {
      loadTitle: 'تنظیمات پروژه بارگذاری نشد',
      loadBody: 'اتصال خود را بررسی و دوباره تلاش کنید.',
      retry: 'تلاش دوباره',
    },
    form: {
      save: 'ذخیره تغییرات',
      saving: 'در حال ذخیره…',
      saved: 'ذخیره شد',
      discard: 'لغو تغییرات',
      unsaved: 'تغییرات ذخیره‌نشده',
      refreshed: 'این پروژه در جای دیگری به‌روز شده است. ویرایش‌های ذخیره‌نشدهٔ شما حفظ شد.',
    },
    general: {
      title: 'عمومی',
      description: 'اطلاعات پایهٔ این پروژه.',
      name: 'نام پروژه',
      descriptionLabel: 'توضیحات',
      counter: '{{count}}/{{max}}',
      status: 'وضعیت',
      statusHelp: {
        ACTIVE: 'پروژه‌های فعال در فهرست پروژه‌های تیم شما نمایش داده می‌شوند.',
        ARCHIVED: 'پروژه‌های بایگانی‌شده در فهرست پروژه‌ها با برچسب بایگانی نشان داده می‌شوند.',
      },
      errors: {
        PROJECT_CONFLICT:
          'این پروژه پس از باز کردن صفحه تغییر کرده است. صفحه را تازه کنید و دوباره تلاش کنید.',
        ACCESS_DENIED: 'اجازهٔ تغییر این پروژه را ندارید.',
        default: 'پروژه ذخیره نشد. دوباره تلاش کنید.',
      },
      success: 'تنظیمات پروژه ذخیره شد.',
    },
    access: {
      title: 'دسترسی',
      description: 'تعیین کنید چه کسانی می‌توانند این پروژه را ببینند و ویرایش کنند.',
      manage: 'مدیریت دسترسی',
      restricted: 'اجازهٔ مدیریت دسترسی این پروژه را ندارید.',
    },
    classification: {
      title: 'طبقه‌بندی داده و سیاست هوش مصنوعی',
      description:
        'طبقه‌بندی تعیین می‌کند کدام ارائه‌دهندگان خارجی هوش مصنوعی و تبدیل متن به گفتار اجازهٔ دریافت محتوای این پروژه را دارند.',
      current: 'فعلی',
      levels: {
        NON_SENSITIVE: {
          label: 'غیرحساس',
          description:
            'محتوای عمومی یا کم‌خطر. می‌تواند به ارائه‌دهندگان خارجی هوش مصنوعی و تبدیل متن به گفتار که شرکت شما برای داده‌های غیرحساس مجاز کرده است ارسال شود.',
        },
        INTERNAL: {
          label: 'داخلی',
          description:
            'محتوای داخلی کسب‌وکار. فقط به ارائه‌دهندگانی ارسال می‌شود که شرکت شما برای داده‌های داخلی تأیید کرده است.',
        },
        CONFIDENTIAL: {
          label: 'محرمانه',
          description:
            'محتوای محدود. فقط به ارائه‌دهندگانی ارسال می‌شود که شرکت شما صراحتاً برای داده‌های محرمانه تأیید کرده است.',
        },
      },
      policyNoteTitle: 'سیاست دادهٔ هوش مصنوعی شرکت شما تصمیم می‌گیرد',
      policyNote:
        'سطح کم‌محدودیت‌تر به‌تنهایی هوش مصنوعی یا روایت را در دسترس نمی‌کند: سیاست دادهٔ هوش مصنوعی و تنظیمات ارائه‌دهندهٔ شرکت شما همچنان اعمال می‌شود. سطح محدودتر می‌تواند هوش مصنوعی و روایت را برای این پروژه متوقف کند.',
      lowerOwnerOnly: 'فقط مالک یا مدیر می‌تواند پروژه را کم‌محدودیت‌تر کند.',
      readOnly: 'می‌توانید طبقه‌بندی را ببینید اما نمی‌توانید آن را تغییر دهید.',
      proposals: {
        title: 'درخواست‌های تغییر طبقه‌بندی',
        description:
          'بازبینی مالک و مدیر فقط زمانی در دسترس است که دروازه‌های پیشنهاد و تصمیم فعال باشند. تغییر طبقه‌بندی ارائه‌دهنده‌ای را فعال نمی‌کند؛ سیاست شرکت همچنان اعمال می‌شود.',
        empty: 'درخواستی برای بازبینی وجود ندارد.',
        loadError: 'بارگذاری درخواست‌های طبقه‌بندی ناموفق بود. دوباره تلاش کنید.',
        transition: 'از {{from}} به {{to}}',
        requester: 'درخواست‌دهنده: {{name}}',
        providerImpact:
          'ارائه‌دهندهٔ فعلی: {{provider}} · سیاست داده: {{policy}}. {{eligible}} واجد شرایط بودن ارائه‌دهندهٔ شرکت و روایت (TTS) در ادامه نشان داده می‌شود و به تأیید گره می‌خورد.',
        mayBecomeEligible:
          'سطح مقصد ممکن است بر اساس سیاست فعلی این پروژه را برای این ارائه‌دهنده واجد شرایط کند.',
        noNewEligibility:
          'این تغییر ارائه‌دهندهٔ هوش مصنوعی فعلی را بر اساس سیاست کنونی واجد شرایط جدید نمی‌کند.',
        eligibility:
          'واجد شرایط بودن متن هوش مصنوعی: {{textBefore}} ← {{textAfter}} · روایت (TTS): {{ttsBefore}} ← {{ttsAfter}}.',
        policyChanged:
          'پیکربندی ارائه‌دهندهٔ هوش مصنوعی یا روایت از زمان ثبت تغییر کرده است. تأیید مسدود است؛ این پیشنهاد را رد و پیشنهاد تازه‌ای ثبت کنید.',
        yes: 'بله',
        no: 'خیر',
        expires: 'مهلت: {{date}}',
        stale: 'پروژه پس از ثبت این درخواست تغییر کرده است؛ امکان اعمال آن نیست.',
        approve: 'اعمال تغییر',
        reject: 'رد درخواست',
        paginationLabel: 'صفحه‌های درخواست‌های در انتظار طبقه‌بندی',
        previousPage: 'صفحهٔ قبل',
        nextPage: 'صفحهٔ بعد',
        pageOf: 'صفحهٔ {{page}} از {{total}}',
        status: {
          APPLIED: 'اعمال‌شده',
          REJECTED: 'ردشده',
          EXPIRED: 'منقضی‌شده',
          PENDING_REVIEW: 'در انتظار بازبینی',
        },
      },
      save: 'ذخیرهٔ طبقه‌بندی',
      saved: 'طبقه‌بندی به‌روز شد.',
      confirmTitle: 'این پروژه کم‌محدودیت‌تر شود؟',
      confirmBody:
        'طبقه‌بندی «{{name}}» از {{from}} به {{to}} تغییر می‌کند. محتوای موجود در این پروژه، از جمله یادداشت‌ها، متن‌ها و دانش، ممکن است واجد شرایط ارسال به ارائه‌دهندگان خارجی هوش مصنوعی و تبدیل متن به گفتار شود. محتوای موجود بازبینی یا تغییر داده نمی‌شود.',
      confirmResponsibility: 'با ادامه تأیید می‌کنید که محتوای این پروژه برای سطح جدید مناسب است.',
      confirmAction: 'کم‌محدودیت‌تر شود',
      errors: {
        ACCESS_DENIED: 'فقط مالک یا مدیر می‌تواند پروژه را کم‌محدودیت‌تر کند.',
        PROJECT_CLASSIFICATION_CONFIRMATION_REQUIRED:
          'برای کم‌محدودیت‌تر کردن این پروژه، تغییر را تأیید کنید.',
        PROJECT_CONFLICT:
          'این پروژه پس از باز کردن صفحه تغییر کرده است. صفحه را تازه کنید و دوباره تلاش کنید.',
        default: 'طبقه‌بندی ذخیره نشد. دوباره تلاش کنید.',
      },
    },
    danger: {
      title: 'ناحیهٔ خطر',
      description: 'اقدام‌های بازگشت‌ناپذیر برای این پروژه.',
      deleteLabel: 'حذف این پروژه',
      deleteHelp:
        'پروژه و ارائه‌های آن را از نمای فعال حذف می‌کند. تاریخچهٔ منتشرشده و تحلیل‌ها ممکن است طبق سیاست نگهداری شوند.',
      deleteButton: 'حذف پروژه',
      dialogTitle: 'پروژه حذف شود؟',
      dialogBody:
        'این کار {{name}} و ارائه‌های آن را از نمای فعال حذف می‌کند. تاریخچهٔ منتشرشده و تحلیل‌ها ممکن است طبق سیاست نگهداری شوند.',
      dialogConfirm: 'حذف پروژه',
      deleting: 'در حال حذف…',
      errors: {
        PROJECT_CONFLICT:
          'این پروژه پس از باز کردن صفحه تغییر کرده است. صفحه را تازه کنید و دوباره تلاش کنید.',
        default: 'پروژه حذف نشد. دوباره تلاش کنید.',
      },
    },
    overview: {
      editClassification: 'ویرایش طبقه‌بندی',
      viewClassification: 'مشاهدهٔ طبقه‌بندی',
      settings: 'تنظیمات پروژه',
    },
    menu: {
      open: 'باز کردن پروژه',
      settings: 'تنظیمات پروژه',
    },
  },
} as const;
